import type { ApiResult } from './types';
import { caseApiRequest } from './apiClient';

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

export type PoliceStationRow = {
  id: string;
  name: string;
  code: string;
  description: string;
  active: boolean;
  orderIndex: number;
  policeDivisionId: string;
  policeDivisionName: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type PoliceDivisionOption = {
  id: string;
  name: string;
  code: string;
  active: boolean;
  districtId: string;
  districtName: string;
};

export type DistrictOption = {
  id: string;
  name: string;
  code: string;
  active: boolean;
};

type ApiPoliceStationRow = {
  id?: string;
  name?: string;
  code?: string;
  description?: string;
  active?: boolean;
  order_index?: number;
  police_division_id?: string;
  police_division_name?: string;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
};

type ApiPoliceDivisionRow = {
  id?: string;
  name?: string;
  code?: string;
  active?: boolean;
  district_id?: string;
  district_name?: string;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
};

type ApiDistrictRow = {
  id?: string;
  name?: string;
  code?: string;
  description?: string;
  active?: boolean;
  provinceId?: string;
};

type ApiPoliceStationPage = {
  content?: ApiPoliceStationRow[];
  totalElements?: number;
  totalPages?: number;
};

export type PoliceStationPageResult = {
  items: PoliceStationRow[];
  total: number;
  totalPages: number;
};

function adaptPoliceStationsPage(
  data: ApiPoliceStationPage | undefined,
  page: number,
  pageSize: number,
): PoliceStationPageResult {
  const rawContent = data?.content ?? [];
  const pageIndex = Math.max(0, page);
  const size = Math.max(1, pageSize);
  const contentLength = rawContent.length;
  const reported =
    typeof data?.totalElements === 'number' && data.totalElements >= 0
      ? data.totalElements
      : contentLength;
  const total = Math.max(reported, contentLength);
  const computedTotalPages = total === 0 ? 1 : Math.max(1, Math.ceil(total / size));
  const apiTotalPages = data?.totalPages;
  const resolvedTotalPages =
    typeof apiTotalPages === 'number' && apiTotalPages > 0 && apiTotalPages >= computedTotalPages
      ? apiTotalPages
      : computedTotalPages;

  const sliced =
    rawContent.length > size ? rawContent.slice(pageIndex * size, pageIndex * size + size) : rawContent;
  const items = sliced
    .map(mapPoliceStationRow)
    .filter((x): x is PoliceStationRow => x != null);

  return { items, total, totalPages: resolvedTotalPages };
}

function mapPoliceStationRow(row: ApiPoliceStationRow): PoliceStationRow | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;
  return {
    id,
    name,
    code: str(row.code),
    description: str(row.description),
    active: row.active !== false,
    orderIndex: typeof row.order_index === 'number' ? row.order_index : 0,
    policeDivisionId: str(row.police_division_id),
    policeDivisionName: str(row.police_division_name),
    createdAt: str(row.created_at),
    createdBy: str(row.created_by),
    updatedAt: str(row.updated_at),
    updatedBy: str(row.updated_by),
  };
}

function mapPoliceDivisionRow(row: ApiPoliceDivisionRow): PoliceDivisionOption | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;
  return {
    id,
    name,
    code: str(row.code),
    active: row.active !== false,
    districtId: str(row.district_id),
    districtName: str(row.district_name),
  };
}

function mapDistrictRow(row: ApiDistrictRow): DistrictOption | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;
  return {
    id,
    name,
    code: str(row.code),
    active: row.active !== false,
  };
}

function buildPoliceStationBody(input: {
  name: string;
  description?: string;
  active?: boolean;
  policeDivisionId: string;
}) {
  const description = input.description?.trim();
  const body: {
    name: string;
    police_division_id: string;
    description?: string;
    active?: boolean;
  } = {
    name: input.name.trim(),
    police_division_id: input.policeDivisionId.trim(),
  };
  if (description) body.description = description;
  if (input.active !== undefined) body.active = input.active;
  return body;
}

export async function listPoliceStations(filters?: {
  active?: boolean;
  name?: string;
  policeDivisionId?: string;
  districtId?: string;
  page?: number;
  pageSize?: number;
}): Promise<ApiResult<PoliceStationPageResult>> {
  const page = filters?.page ?? 0;
  const pageSize = filters?.pageSize ?? 20;
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('size', String(pageSize));
  if (filters?.active !== undefined) qs.set('active', String(filters.active));
  const name = filters?.name?.trim();
  if (name) qs.set('name', name);
  const policeDivisionId = filters?.policeDivisionId?.trim();
  if (policeDivisionId) qs.set('policeDivisionId', policeDivisionId);
  const districtId = filters?.districtId?.trim();
  if (districtId) qs.set('districtId', districtId);
  const res = await caseApiRequest<ApiPoliceStationPage>(`/v1/masters/police-stations?${qs.toString()}`);
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: adaptPoliceStationsPage(res.data, page, pageSize) };
}

export async function getPoliceStationById(id: string): Promise<ApiResult<PoliceStationRow>> {
  const stationId = id.trim();
  if (!stationId) return { success: false, message: 'Police station id is required.' };
  const res = await caseApiRequest<ApiPoliceStationRow>(
    `/v1/masters/police-stations/${encodeURIComponent(stationId)}`,
  );
  if (!res.ok) return { success: false, message: res.message };
  const row = mapPoliceStationRow(res.data ?? {});
  if (!row) return { success: false, message: 'Invalid police station response.' };
  return { success: true, data: row };
}

export async function createPoliceStation(input: {
  name: string;
  description?: string;
  active?: boolean;
  policeDivisionId: string;
}): Promise<ApiResult<PoliceStationRow>> {
  const res = await caseApiRequest<ApiPoliceStationRow>('/v1/masters/police-stations', {
    method: 'POST',
    body: buildPoliceStationBody(input),
  });
  if (!res.ok) return { success: false, message: res.message };
  const row = mapPoliceStationRow(res.data ?? {});
  if (!row) return { success: false, message: 'Invalid create response.' };
  return { success: true, data: row };
}

export async function updatePoliceStation(
  id: string,
  input: {
    name: string;
    description?: string;
    active?: boolean;
    policeDivisionId: string;
  },
): Promise<ApiResult<PoliceStationRow>> {
  const stationId = id.trim();
  if (!stationId) return { success: false, message: 'Police station id is required.' };
  const res = await caseApiRequest<ApiPoliceStationRow>(
    `/v1/masters/police-stations/${encodeURIComponent(stationId)}`,
    {
      method: 'PUT',
      body: buildPoliceStationBody(input),
    },
  );
  if (!res.ok) return { success: false, message: res.message };
  const row = mapPoliceStationRow(res.data ?? {});
  if (!row) return { success: false, message: 'Invalid update response.' };
  return { success: true, data: row };
}

export async function listPoliceDivisions(filters?: {
  active?: boolean;
  name?: string;
  districtId?: string;
}): Promise<ApiResult<PoliceDivisionOption[]>> {
  const qs = new URLSearchParams();
  if (filters?.active !== undefined) qs.set('active', String(filters.active));
  const name = filters?.name?.trim();
  if (name) qs.set('name', name);
  const districtId = filters?.districtId?.trim();
  if (districtId) qs.set('districtId', districtId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await caseApiRequest<ApiPoliceDivisionRow[]>(`/v1/masters/police-divisions${suffix}`);
  if (!res.ok) return { success: false, message: res.message };
  const rows = (Array.isArray(res.data) ? res.data : [])
    .map(mapPoliceDivisionRow)
    .filter((x): x is PoliceDivisionOption => x != null);
  return { success: true, data: rows };
}

export async function listAllDistricts(): Promise<ApiResult<DistrictOption[]>> {
  const res = await caseApiRequest<ApiDistrictRow[]>('/v1/case-requests/districts');
  if (!res.ok) return { success: false, message: res.message };
  const rows = (Array.isArray(res.data) ? res.data : [])
    .map(mapDistrictRow)
    .filter((x): x is DistrictOption => x != null);
  return { success: true, data: rows };
}
